/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import React from 'react';

import {
    getInputWithoutFormatting,
    IRefineItemData,
    maxInputClassName,
    maxInputLabelId,
    maxLabelClassName,
    maxLabelId,
    minInputClassName,
    minInputLabelId,
    minLabelClassName,
    minLabelId
} from './common';

/**
 * Range refine item type.
 */
export type RangeRefineItemType = 'slider' | 'input';

/**
 * Range refine input type.
 */
type InputType = 'Min' | 'Max';

let minInput: React.RefObject<HTMLInputElement>;

let maxInput: React.RefObject<HTMLInputElement>;

const minInputLabel = 'Minimum';
const maxInputLabel = 'Maximum';

/**
 * IRangeRefineInputComponent Interface.
 */
export interface IRangeRefineInputComponent extends IComponent<IRangeRefineInputProps> {}

/**
 * IRangeRefineInput Data interface.
 */
export interface IRefinerItemData {
    refinerItemData: IRefineItemData;
}

/**
 * IRangeRefineInput Item props Interface.
 */
export interface IRangeRefineInputProps extends IComponentProps<IRefinerItemData> {
    minRangeValue: string;
    maxRangeValue: string;
    className?: string;
    isDisabled?: boolean;
    inputMinLabel?: string;
    inputMaxLabel?: string;
    minInputAriaLabel?: string;
    maxInputAriaLabel?: string;
    validationErrorRange?: string;
    onChangeMinValue?(changeMinValue: string): void;
    onChangeMaxValue?(changeMaxValue: string): void;
    onBlurMinInput?(BlurMinValue: number): void;
    onBlurMaxInput?(BlurMaxValue: number): void;
    formattedSelectedValue?(value: string, isTouched: boolean, validationError: string): string;
}

/**
 * Range refine input component.
 * @param props - IRangeRefineInputProps.
 * @returns - Renders the Range Refine Input.
 */
const RangeRefineInputFunctionalComponent: React.FC<IRangeRefineInputProps> = (props: IRangeRefineInputProps) => {
    const {
        minRangeValue,
        maxRangeValue,
        isDisabled,
        inputMinLabel,
        inputMaxLabel,
        validationErrorRange,
        minInputAriaLabel,
        maxInputAriaLabel,
        onChangeMinValue,
        onChangeMaxValue,
        onBlurMinInput,
        onBlurMaxInput
    } = props;
    const [selectedMinValue, setSelectedMinValue] = React.useState(props.minRangeValue);
    const [selectedMaxValue, setSelectedMaxValue] = React.useState(props.maxRangeValue);
    const [validationErrorMessageForRange, setValidationErrorMessageForRange] = React.useState('');

    const formAttributes = {
        'aria-disabled': isDisabled
    };

    /**
     * Change Input value.
     * @param event - Focus Event.
     * @param inputType - Input type.
     */
    function changeValue(event: React.FocusEvent<HTMLInputElement>, inputType: InputType): void {
        const selectedKey = `selected${inputType}`;
        const selectedFormattedValue = getInputWithoutFormatting(event.currentTarget.value);
        if (selectedKey === 'selectedMin') {
            setSelectedMinValue(selectedFormattedValue);
            onChangeMinValue?.(selectedFormattedValue);
        } else {
            setSelectedMaxValue(selectedFormattedValue);
            onChangeMaxValue?.(selectedFormattedValue);
        }
    }

    /**
     * Change min Input value.
     * @param event - Focus Event.
     */
    const changeMinValue = (event: React.FocusEvent<HTMLInputElement>): void => {
        changeValue(event, 'Min');
    };

    /**
     * Change max Input value.
     * @param event - Focus Event.
     */
    const changeMaxValue = (event: React.FocusEvent<HTMLInputElement>): void => {
        changeValue(event, 'Max');
    };

    /**
     * VAlidate Range value.
     * @param minimum - Minimum value.
     * @param maximum - Maximum value.
     * @returns - Boolean value to validate range or not.
     */
    function validateRange(minimum: number, maximum: number): boolean {
        if (minimum > maximum) {
            setValidationErrorMessageForRange(validationErrorRange ?? 'Please Enter max value greater than min value');
            return false;
        }
        setValidationErrorMessageForRange('');
        return true;
    }

    /**
     * Finish min Input value.
     * @param event - Focus Event.
     */
    const finishChangeMin = (event: React.FocusEvent<HTMLInputElement>) => {
        const userSelectedMinValue = getInputWithoutFormatting(event.currentTarget.value);
        setSelectedMinValue(userSelectedMinValue);
        const updatedMinInput = Number(userSelectedMinValue);
        const maximum = maxRangeValue;

        const maxNumber = Number(maximum);

        if (validateRange(updatedMinInput, maxNumber)) {
            onBlurMinInput?.(updatedMinInput);
        }
    };

    /**
     * Finish max Input value.
     * @param event - Focus Event.
     */
    const finishChangeMax = (event: React.FocusEvent<HTMLInputElement>) => {
        const userSelectedMaxValue = getInputWithoutFormatting(event.currentTarget.value);
        setSelectedMaxValue(userSelectedMaxValue);
        const updatedMaxInput = Number(userSelectedMaxValue);
        const minNumber = Number(minRangeValue || '0');

        if (validateRange(minNumber, updatedMaxInput)) {
            onBlurMaxInput?.(updatedMaxInput);
        }
    };

    return (
        <form className='ms-refine-submenu__input-range-refiner' {...formAttributes} data-testid='range-refine-input-item-container'>
            <label id={minLabelId} htmlFor={minInputLabelId} className={minLabelClassName}>
                {inputMinLabel ?? minInputLabel}
                <input
                    className={minInputClassName}
                    type='number'
                    onChange={changeMinValue}
                    onBlur={finishChangeMin}
                    value={selectedMinValue}
                    id={minInputLabelId}
                    ref={minInput}
                    aria-label={minInputAriaLabel}
                />
            </label>
            <label id={maxLabelId} htmlFor={maxInputLabelId} className={maxLabelClassName}>
                {inputMaxLabel ?? maxInputLabel}
                <input
                    className={maxInputClassName}
                    type='number'
                    placeholder='Please Enter max value'
                    onChange={changeMaxValue}
                    onBlur={finishChangeMax}
                    value={selectedMaxValue}
                    id={maxInputLabelId}
                    ref={maxInput}
                    aria-label={maxInputAriaLabel}
                />
            </label>
            {validationErrorMessageForRange && validationErrorMessageForRange !== '' && (
                <span className='ms-refine-submenu__input-range-error-text refine-submenu__input-range-max-error-text'>
                    {validationErrorMessageForRange}
                </span>
            )}
        </form>
    );
};

/**
 * Range Refine Input Component.
 */
export const RangeRefineInputComponent: React.FunctionComponent<IRangeRefineInputProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error -- Required for this signature.
    IRangeRefineInputComponent
>('Range-Refine-Input-Item', { component: React.memo(RangeRefineInputFunctionalComponent) });
